import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Receipt, Plus } from "lucide-react";

export default function FixerInvoices() {
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/fixer/orders").then((r) => setOrders(r.data));
    api.get("/fixer/invoices").then((r) => setInvoices(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Eligible: completed or sent_back, not already invoiced
  const invoicedOrderIds = useMemo(
    () => new Set(invoices.flatMap((i) => i.order_ids || [])),
    [invoices],
  );
  const eligible = orders.filter(
    (o) => (o.status === "completed" || o.status === "sent_back") && !invoicedOrderIds.has(o.id),
  );

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const total = useMemo(
    () => eligible.filter((o) => selected.has(o.id)).reduce((s, o) => s + (o.service_fee || 0), 0),
    [eligible, selected],
  );

  const submit = async () => {
    if (selected.size === 0 || total <= 0) {
      toast.error("Velg minst én bestilling med arbeidsbeløp.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/fixer/invoices", {
        order_ids: Array.from(selected),
        amount: total,
        notes: notes || undefined,
      });
      toast.success("Faktura sendt til Tekkdeler");
      setSelected(new Set());
      setNotes("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tk-container py-16" data-testid="fixer-invoices-page">
      <p className="tk-overline mb-3">Fakturering</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-2">Send faktura til Tekkdeler</h1>
      <p className="text-sm text-slate-500 mb-10 max-w-xl">
        Velg fullførte oppdrag, så lager vi en faktura som Tekkdeler betaler ut til bankkontoen din.
      </p>

      <div className="tk-card mb-10">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Plus size={16} /> Ny faktura
        </h3>
        {eligible.length === 0 ? (
          <p className="text-sm text-slate-500">Ingen ufakturerte oppdrag.</p>
        ) : (
          <>
            <div className="space-y-2 mb-4">
              {eligible.map((o) => (
                <label
                  key={o.id}
                  className={`flex items-center justify-between px-4 py-3 rounded-md border cursor-pointer ${selected.has(o.id) ? "border-[#0B1C30] bg-slate-50" : "border-[#E2E8F0] bg-white"}`}
                  data-testid={`invoice-eligible-${o.id}`}
                >
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} />
                    <div>
                      <p className="font-medium text-sm">{o.product_name}</p>
                      <p className="text-xs text-slate-500">#{o.id.slice(0, 8)} · {o.service_name}</p>
                    </div>
                  </div>
                  <p className="font-semibold text-sm">kr {Number(o.service_fee).toFixed(0)}</p>
                </label>
              ))}
            </div>
            <textarea
              placeholder="Notat (valgfritt) — referansenummer, særskilte forhold mm."
              className="tk-input min-h-[80px] mb-4"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="invoice-notes"
            />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Totalbeløp</p>
                <p className="text-2xl font-semibold">kr {total.toFixed(0)}</p>
              </div>
              <button onClick={submit} disabled={busy} className="tk-btn-primary" data-testid="submit-invoice-btn">
                {busy ? "Sender …" : `Send faktura (${selected.size})`}
              </button>
            </div>
          </>
        )}
      </div>

      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <Receipt size={18} /> Tidligere fakturaer
      </h2>
      {invoices.length === 0 ? (
        <p className="text-slate-500">Ingen fakturaer ennå.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-history-table">
            <thead className="text-left">
              <tr className="border-b border-[#E2E8F0]">
                <th className="py-3 pr-4 font-medium text-slate-500">Dato</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Antall oppdrag</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Beløp</th>
                <th className="py-3 pr-4 font-medium text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-[#E2E8F0]/70 hover:bg-slate-50" data-testid={`invoice-${inv.id}`}>
                  <td className="py-3 pr-4 text-slate-500">
                    <Link to={`/verksted/faktura/${inv.id}`} className="text-[#0B1C30] hover:underline" data-testid={`invoice-link-${inv.id}`}>
                      {new Date(inv.created_at).toLocaleDateString("nb-NO")}
                    </Link>
                  </td>
                  <td className="py-3 pr-4">{(inv.order_ids || []).length}</td>
                  <td className="py-3 pr-4 font-medium">kr {Number(inv.amount).toFixed(0)}</td>
                  <td className="py-3 pr-4">
                    <span className={`text-xs px-2 py-1 rounded-md ${inv.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {inv.status === "paid" ? "Betalt" : "Sendt"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
